import React, {
  createContext,
  useReducer,
  useCallback,
  useEffect,
} from "react";
import FieldConfigurationAPI from "../api/FieldConfiguration";

// Action types
const ACTIONS = {
  // Roles
  SET_ROLES: "SET_ROLES",
  ADD_ROLE: "ADD_ROLE",
  UPDATE_ROLE: "UPDATE_ROLE",
  DELETE_ROLE: "DELETE_ROLE",
  SET_ROLES_LOADING: "SET_ROLES_LOADING",
  SET_ROLES_ERROR: "SET_ROLES_ERROR",

  // Users
  SET_USERS: "SET_USERS",
  ADD_USER: "ADD_USER",
  UPDATE_USER: "UPDATE_USER",
  DELETE_USER: "DELETE_USER",
  SET_USERS_LOADING: "SET_USERS_LOADING",
  SET_USERS_ERROR: "SET_USERS_ERROR",

  // Licenses
  SET_LICENSES: "SET_LICENSES",
  SET_LICENSES_LOADING: "SET_LICENSES_LOADING",
  SET_LICENSES_ERROR: "SET_LICENSES_ERROR",

  // Admin permissions
  SET_ADMIN_PERMISSIONS: "SET_ADMIN_PERMISSIONS",
  SET_ADMIN_PERMISSIONS_LOADING: "SET_ADMIN_PERMISSIONS_LOADING",

  // Companies
  SET_COMPANIES: "SET_COMPANIES",

  // Cache control
  INVALIDATE_CACHE: "INVALIDATE_CACHE",
};

// Initial state
const initialState = {
  roles: {
    data: [],
    loading: false,
    error: null,
    lastFetched: null,
  },
  users: {
    data: [],
    loading: false,
    error: null,
    lastFetched: null,
  },
  adminPermissions: {
    data: null,
    loading: false,
    loaded: false,
    error: null,
  },

  companies: {
    data: {}, // { [uuid]: name } — all accessible companies
    configureRolesData: {}, // { [uuid]: name } — companies where user has configure_roles
    configureUsersData: {}, // { [uuid]: name } — companies where user has configure_users
    codesMap: {}, // { [uuid]: code } — company codes
  },

  // Extensible for future features
  accessGroups: {
    data: [],
    loading: false,
    error: null,
    lastFetched: null,
  },
  licenses: {
    data: [],
    loading: false,
    error: null,
    lastFetched: null,
  },
};

// Reducer
function fieldConfigurationReducer(state, action) {
  switch (action.type) {
    // Roles actions
    case ACTIONS.SET_ROLES:
      return {
        ...state,
        roles: {
          ...state.roles,
          data: action.payload,
          loading: false,
          error: null,
          lastFetched: Date.now(),
        },
      };

    case ACTIONS.ADD_ROLE:
      return {
        ...state,
        roles: {
          ...state.roles,
          data: [...state.roles.data, action.payload],
        },
      };

    case ACTIONS.UPDATE_ROLE:
      return {
        ...state,
        roles: {
          ...state.roles,
          data: state.roles.data.map((role) =>
            role.uuid === action.payload.uuid ? action.payload : role
          ),
        },
      };

    case ACTIONS.DELETE_ROLE:
      return {
        ...state,
        roles: {
          ...state.roles,
          data: state.roles.data.filter((role) => role.uuid !== action.payload),
        },
      };

    case ACTIONS.SET_ROLES_LOADING:
      return {
        ...state,
        roles: {
          ...state.roles,
          loading: action.payload,
        },
      };

    case ACTIONS.SET_ROLES_ERROR:
      return {
        ...state,
        roles: {
          ...state.roles,
          loading: false,
          error: action.payload,
        },
      };

    // Users actions
    case ACTIONS.SET_USERS:
      return {
        ...state,
        users: {
          ...state.users,
          data: action.payload,
          loading: false,
          error: null,
          lastFetched: Date.now(),
        },
      };

    case ACTIONS.ADD_USER:
      return {
        ...state,
        users: {
          ...state.users,
          data: [...state.users.data, action.payload],
        },
      };

    case ACTIONS.UPDATE_USER:
      return {
        ...state,
        users: {
          ...state.users,
          data: state.users.data.map((user) =>
            user.uuid === action.payload.uuid ? action.payload : user
          ),
        },
      };

    case ACTIONS.DELETE_USER:
      return {
        ...state,
        users: {
          ...state.users,
          data: state.users.data.filter((user) => user.uuid !== action.payload),
        },
      };

    case ACTIONS.SET_USERS_LOADING:
      return {
        ...state,
        users: {
          ...state.users,
          loading: action.payload,
        },
      };

    case ACTIONS.SET_USERS_ERROR:
      return {
        ...state,
        users: {
          ...state.users,
          loading: false,
          error: action.payload,
        },
      };

    // Licenses actions
    case ACTIONS.SET_LICENSES:
      return {
        ...state,
        licenses: {
          ...state.licenses,
          data: action.payload,
          loading: false,
          error: null,
          lastFetched: Date.now(),
        },
      };

    case ACTIONS.SET_LICENSES_LOADING:
      return {
        ...state,
        licenses: {
          ...state.licenses,
          loading: action.payload,
        },
      };

    case ACTIONS.SET_LICENSES_ERROR:
      return {
        ...state,
        licenses: {
          ...state.licenses,
          loading: false,
          error: action.payload,
        },
      };

    case ACTIONS.SET_ADMIN_PERMISSIONS:
      return {
        ...state,
        adminPermissions: {
          data: action.payload,
          loading: false,
          loaded: true,
          error: null,
        },
      };

    case ACTIONS.SET_ADMIN_PERMISSIONS_LOADING:
      return {
        ...state,
        adminPermissions: {
          ...state.adminPermissions,
          loading: action.payload,
        },
      };

    case ACTIONS.SET_COMPANIES:
      return {
        ...state,
        companies: {
          data: action.payload.data || {},
          configureRolesData: action.payload.configureRolesData || {},
          configureUsersData: action.payload.configureUsersData || {},
          codesMap: action.payload.codesMap || {},
        },
      };

    // Cache control
    case ACTIONS.INVALIDATE_CACHE:
      return {
        ...state,
        roles: { ...state.roles, lastFetched: null },
        users: { ...state.users, lastFetched: null },
        licenses: { ...state.licenses, lastFetched: null },
      };

    default:
      return state;
  }
}

// Helper functions to transform between API format and UI format for ROLES
const transformApiRoleToUi = (apiRole) => {
  if (!apiRole) return null;

  const permissions = apiRole.permissions || {};
  const timesheetPerms = permissions.timesheets || {};
  const projectmgmtPerms = permissions.projectmgmt || {};
  const showFields = timesheetPerms.show || {};
  const requireFields = timesheetPerms.require || {};
  const payTypes = timesheetPerms.pay_types || {};
  const timesheetSettings = apiRole.timesheet_settings || {};
  const administrationPerms = permissions.administration || {};

  return {
    ...apiRole,
    // Module enablement flags
    enableTime: apiRole.timesheets || false,
    enableExpense: apiRole.expenses || false,
    enableProjectManagement: apiRole.projectmgmt || false,

    // Date mapping
    created: apiRole.created_at,
    updated: apiRole.updated_at,

    // User counts (API returns as strings, convert to numbers)
    totalUsers: parseInt(apiRole.total_users || "0", 10),
    activeUsers: parseInt(apiRole.active_users || "0", 10),

    // General settings
    enableJobLocationEditing: permissions.can_edit_job_locations || false,
    enableAdministration: administrationPerms.enabled || false,
    adminViewLicenses: administrationPerms.view_licenses || false,
    adminConfigureAccessGroups:
      administrationPerms.configure_access_groups || false,
    adminConfigureRoles: administrationPerms.configure_roles || false,
    adminConfigureUsers: administrationPerms.configure_users || false,

    // Time Entry settings
    allowMultipleEmployeeEntries:
      timesheetPerms.multiple_employee_entry || false,
    allowSigningOtherEmployees: timesheetPerms.can_sign_for_others || false,
    showMyEmployeesWeeklySummary:
      timesheetPerms.can_view_others_weekly_time || false,
    showMyEmployeesWorkerClass: showFields.other_worker_class || false,
    allowApprovals: timesheetPerms.can_approve || false,
    allowAbsenceEntries: timesheetPerms.allow_absence_entries || false,
    viewAndSignOnly: timesheetPerms.view_and_sign_only || false,
    showPayType: showFields.pay_type || false,
    payTypeOptions: {
      regular: payTypes.regular || false,
      overtime: payTypes.overtime || false,
      double: payTypes.double || false,
      sick: payTypes.sick || false,
      vacation: payTypes.vacation || false,
      holiday: payTypes.holiday || false,
      bonus: payTypes.bonus || false,
      other: payTypes.other || false,
      nontax: payTypes.nontax || false,
    },
    requireTimer: showFields.timer || false,
    requireJob: requireFields.job || false,
    showWorkersClass: showFields.class || false,
    showDepartment: showFields.department || false,
    showWorkLocation: showFields.work_location || false,
    showEquipment: showFields.equipment || false,
    showUnits: showFields.units || false,
    timerAlertMinutes: timesheetSettings.geotolerance_minutes || 0,
    hoursAlertHours: timesheetSettings.dailytolerance_hours || 0,

    // Project Management settings
    allowFieldLogs: projectmgmtPerms.allow_field_logs || false,
  };
};

const transformUiRoleToApi = (uiRole) => {
  if (!uiRole) return null;

  return {
    name: uiRole.name,
    description: uiRole.description || null,
    timesheets: uiRole.enableTime || false,
    expenses: uiRole.enableExpense || false,
    projectmgmt: uiRole.enableProjectManagement || false,
    fieldlogs: uiRole.allowFieldLogs || false,
    permissions: {
      timesheets: {
        show: {
          class: uiRole.showWorkersClass || false,
          timer: uiRole.requireTimer || false,
          units: uiRole.showUnits || false,
          pay_type: uiRole.showPayType || false,
          equipment: uiRole.showEquipment || false,
          department: uiRole.showDepartment || false,
          work_location: uiRole.showWorkLocation || false,
          other_worker_class: uiRole.showMyEmployeesWorkerClass || false,
        },
        require: {
          job: uiRole.requireJob || false,
        },
        pay_types: {
          regular: uiRole.payTypeOptions?.regular || false,
          overtime: uiRole.payTypeOptions?.overtime || false,
          double: uiRole.payTypeOptions?.double || false,
          sick: uiRole.payTypeOptions?.sick || false,
          vacation: uiRole.payTypeOptions?.vacation || false,
          holiday: uiRole.payTypeOptions?.holiday || false,
          bonus: uiRole.payTypeOptions?.bonus || false,
          other: uiRole.payTypeOptions?.other || false,
          nontax: uiRole.payTypeOptions?.nontax || false,
        },
        can_approve: uiRole.allowApprovals || false,
        group_entry: uiRole.allowMultipleEmployeeEntries || false,
        allow_absence_entries: uiRole.allowAbsenceEntries || false,
        view_and_sign_only: uiRole.viewAndSignOnly || false,
        can_sign_for_others: uiRole.allowSigningOtherEmployees || false,
        multiple_employee_entry: uiRole.allowMultipleEmployeeEntries || false,
        can_view_others_weekly_time:
          uiRole.showMyEmployeesWeeklySummary || false,
      },
      projectmgmt: {
        allow_field_logs: uiRole.allowFieldLogs || false,
      },
      can_edit_job_locations: uiRole.enableJobLocationEditing || false,
      administration: {
        enabled: uiRole.enableAdministration || false,
        view_licenses: uiRole.adminViewLicenses || false,
        configure_access_groups: uiRole.adminConfigureAccessGroups || false,
        configure_roles: uiRole.adminConfigureRoles || false,
        configure_users: uiRole.adminConfigureUsers || false,
      },
    },
    timesheet_settings: {
      dailytolerance_hours: uiRole.hoursAlertHours || 0,
      geotolerance_minutes: uiRole.timerAlertMinutes || 0,
    },
  };
};

// Helper functions to transform between API format and UI format for USERS
const transformApiUserToUi = (apiUser, rolesMap = {}) => {
  if (!apiUser) return null;

  // Get role name from role_uuid
  const roleName = apiUser.role_uuid ? rolesMap[apiUser.role_uuid]?.name : null;

  return {
    ...apiUser,
    firstName: apiUser.first_name,
    lastName: apiUser.last_name,
    employee: apiUser.code,
    mobileNumber: apiUser.mobile_phone,
    active: apiUser.enabled,
    role: roleName || apiUser.role_uuid,
    roleUuid: apiUser.role_uuid,
    lastLogin: apiUser.last_login || null,
    // Groups and settings
    groups: apiUser.groups || [],
    settings: apiUser.settings || null,
  };
};

const transformUiUserToApi = (uiUser) => {
  if (!uiUser) return null;

  const apiData = {
    first_name: uiUser.firstName,
    last_name: uiUser.lastName,
    email: uiUser.email,
    code: uiUser.employee || uiUser.code || "",
    enabled: uiUser.active,
    role_uuid: uiUser.role || uiUser.role_uuid,
  };

  // Only include mobile_phone if it has a value
  if (uiUser.mobileNumber) {
    apiData.mobile_phone = uiUser.mobileNumber;
  }

  // Include groups if present
  if (uiUser.groups && Array.isArray(uiUser.groups)) {
    // API expects array of group objects with ONLY {uuid, name} format (no extra fields)
    // The Joi validation compares against exact objects fetched from DB
    apiData.groups = uiUser.groups.map((group) => {
      if (typeof group === "string") {
        // If it's just a UUID string, we can't validate properly without name
        // This shouldn't happen in normal flow
        return { uuid: group };
      } else if (group && group.uuid) {
        // Strip any extra fields (like access_codes) and only send uuid and name
        return {
          uuid: group.uuid,
          name: group.name,
        };
      }
      return group;
    });
  }

  // Include settings if present
  if (uiUser.settings) {
    apiData.settings = uiUser.settings;
  }

  return apiData;
};

// Create context
export const FieldConfigurationContext = createContext();

// Provider component
export function FieldConfigurationProvider({ children }) {
  const [state, dispatch] = useReducer(fieldConfigurationReducer, initialState);

  // Cache duration in milliseconds (5 minutes)
  const CACHE_DURATION = 5 * 60 * 1000;

  // Helper to check if cache is valid
  const isCacheValid = (lastFetched) => {
    if (!lastFetched) return false;
    return Date.now() - lastFetched < CACHE_DURATION;
  };

  // Helper to check for 401 Unauthorized and redirect to login
  const checkAuthAndRedirect = (response) => {
    if (response.status === 401) {
      console.trace("[FieldConfig] checkAuthAndRedirect: 401 triggered logout");
      // Clear session storage
      sessionStorage.clear();
      // Redirect to login
      window.location.href = "/login";
      return true;
    }
    return false;
  };

  // ============================================
  // ROLES METHODS
  // ============================================

  const fetchRoles = useCallback(
    async (forceRefresh = false) => {
      // Use cache if valid and not forcing refresh
      if (!forceRefresh && isCacheValid(state.roles.lastFetched)) {
        return { success: true, data: state.roles.data };
      }

      dispatch({ type: ACTIONS.SET_ROLES_LOADING, payload: true });

      try {
        const response = await FieldConfigurationAPI.getRoles();

        // Check for 401 and redirect if unauthorized
        if (checkAuthAndRedirect(response)) {
          return { success: false, error: "Unauthorized" };
        }

        if (response.status === 200) {
          // Transform API format to UI format
          const transformedRoles = Array.isArray(response.data)
            ? response.data.map(transformApiRoleToUi)
            : [];
          dispatch({ type: ACTIONS.SET_ROLES, payload: transformedRoles });
          return { success: true, data: transformedRoles };
        } else {
          const error = `Failed to fetch roles: ${response.status}`;
          dispatch({ type: ACTIONS.SET_ROLES_ERROR, payload: error });
          return { success: false, error };
        }
      } catch (error) {
        const errorMsg = error.message || "Unknown error fetching roles";
        dispatch({ type: ACTIONS.SET_ROLES_ERROR, payload: errorMsg });
        return { success: false, error: errorMsg };
      }
    },
    [state.roles.lastFetched, state.roles.data]
  );

  const createRole = useCallback(async (roleData) => {
    dispatch({ type: ACTIONS.SET_ROLES_LOADING, payload: true });

    try {
      // Transform UI format to API format
      const apiRoleData = transformUiRoleToApi(roleData);
      const response = await FieldConfigurationAPI.createRole(apiRoleData);

      // Check for 401 and redirect if unauthorized
      if (checkAuthAndRedirect(response)) {
        return { success: false, error: "Unauthorized" };
      }

      if (response.status === 200 || response.status === 201) {
        // Transform API response back to UI format
        const transformedRole = transformApiRoleToUi(response.data);
        dispatch({ type: ACTIONS.ADD_ROLE, payload: transformedRole });
        dispatch({ type: ACTIONS.SET_ROLES_LOADING, payload: false });
        return { success: true, data: transformedRole };
      } else if (response.status === 403 && response.data) {
        // License limit error - return detailed error information
        dispatch({ type: ACTIONS.SET_ROLES_LOADING, payload: false });
        return {
          success: false,
          status: 403,
          error: response.data.message || "Insufficient license seats",
          errors: response.data.errors || [],
          details: transformLicenseDetails(response.data.details),
        };
      } else {
        const error = `Failed to create role: ${response.status}`;
        dispatch({ type: ACTIONS.SET_ROLES_ERROR, payload: error });
        return { success: false, error };
      }
    } catch (error) {
      const errorMsg = error.message || "Unknown error creating role";
      dispatch({ type: ACTIONS.SET_ROLES_ERROR, payload: errorMsg });
      return { success: false, error: errorMsg };
    }
  }, []);

  const updateRole = useCallback(async (uuid, roleData) => {
    dispatch({ type: ACTIONS.SET_ROLES_LOADING, payload: true });

    try {
      // Transform UI format to API format
      const apiRoleData = transformUiRoleToApi(roleData);
      const response = await FieldConfigurationAPI.updateRole(
        uuid,
        apiRoleData
      );

      // Check for 401 and redirect if unauthorized
      if (checkAuthAndRedirect(response)) {
        return { success: false, error: "Unauthorized" };
      }

      if (response.status === 200) {
        // Transform API response back to UI format
        const transformedRole = transformApiRoleToUi(response.data);
        dispatch({ type: ACTIONS.UPDATE_ROLE, payload: transformedRole });
        await fetchLicenses(true);
        dispatch({ type: ACTIONS.SET_ROLES_LOADING, payload: false });
        return { success: true, data: transformedRole };
      } else if (response.status === 403 && response.data) {
        // License limit error - return detailed error information
        dispatch({ type: ACTIONS.SET_ROLES_LOADING, payload: false });
        return {
          success: false,
          status: 403,
          error: response.data.message || "Insufficient license seats",
          errors: response.data.errors || [],
          details: transformLicenseDetails(response.data.details),
        };
      } else {
        const error = `Failed to update role: ${response.status}`;
        dispatch({ type: ACTIONS.SET_ROLES_ERROR, payload: error });
        return { success: false, error };
      }
    } catch (error) {
      const errorMsg = error.message || "Unknown error updating role";
      dispatch({ type: ACTIONS.SET_ROLES_ERROR, payload: errorMsg });
      return { success: false, error: errorMsg };
    }
  }, []);

  const deleteRole = useCallback(async (uuid) => {
    dispatch({ type: ACTIONS.SET_ROLES_LOADING, payload: true });

    try {
      const response = await FieldConfigurationAPI.deleteRole(uuid);

      // Check for 401 and redirect if unauthorized
      if (checkAuthAndRedirect(response)) {
        return { success: false, error: "Unauthorized" };
      }

      if (response.status === 200 || response.status === 204) {
        dispatch({ type: ACTIONS.DELETE_ROLE, payload: uuid });
        dispatch({ type: ACTIONS.SET_ROLES_LOADING, payload: false });
        return { success: true };
      } else {
        const error = `Failed to delete role: ${response.status}`;
        dispatch({ type: ACTIONS.SET_ROLES_ERROR, payload: error });
        return { success: false, error };
      }
    } catch (error) {
      const errorMsg = error.message || "Unknown error deleting role";
      dispatch({ type: ACTIONS.SET_ROLES_ERROR, payload: errorMsg });
      return { success: false, error: errorMsg };
    }
  }, []);

  const bulkUpdateRoles = useCallback(async (uuids, roleData) => {
    dispatch({ type: ACTIONS.SET_ROLES_LOADING, payload: true });

    try {
      const apiRoleData = transformUiRoleToApi(roleData);
      const results = await Promise.all(
        uuids.map((uuid) => FieldConfigurationAPI.updateRole(uuid, apiRoleData))
      );

      // Check for 401
      const unauthorizedResult = results.find((r) => r.status === 401);
      if (unauthorizedResult && checkAuthAndRedirect(unauthorizedResult)) {
        return { success: false, error: "Unauthorized" };
      }

      // Check for 403 (license errors)
      const licenseErrorResult = results.find(
        (r) => r.status === 403 && r.data
      );
      if (licenseErrorResult) {
        dispatch({ type: ACTIONS.SET_ROLES_LOADING, payload: false });
        return {
          success: false,
          status: 403,
          error:
            licenseErrorResult.data.message || "Insufficient license seats",
          errors: licenseErrorResult.data.errors || [],
          details: transformLicenseDetails(licenseErrorResult.data.details),
        };
      }

      const allSucceeded = results.every((r) => r.status === 200);
      if (allSucceeded) {
        const rolesResponse = await FieldConfigurationAPI.getRoles();
        if (rolesResponse.status === 200) {
          const transformedRoles = Array.isArray(rolesResponse.data)
            ? rolesResponse.data.map(transformApiRoleToUi)
            : [];
          dispatch({ type: ACTIONS.SET_ROLES, payload: transformedRoles });
        }
        await fetchLicenses(true);
        return { success: true };
      } else {
        const failedResult = results.find((r) => r.status !== 200);
        const error = `Failed to update role: ${failedResult?.status}`;
        dispatch({ type: ACTIONS.SET_ROLES_ERROR, payload: error });
        return { success: false, error };
      }
    } catch (error) {
      const errorMsg = error.message || "Unknown error updating roles";
      dispatch({ type: ACTIONS.SET_ROLES_ERROR, payload: errorMsg });
      return { success: false, error: errorMsg };
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Saves a role with company assignment changes.
  // selectedCompanyUuids = companies that should be assigned after save.
  // manageableCompanyUuids = all companies where user has configure_roles.
  const saveRoleWithCompanyAssignments = useCallback(
    async (
      role,
      formData,
      selectedCompanyUuids,
      manageableCompanyUuids,
      companiesMap
    ) => {
      dispatch({ type: ACTIONS.SET_ROLES_LOADING, payload: true });

      try {
        const apiRoleData = transformUiRoleToApi(formData);

        // Build map of companyUuid -> role UUID for existing assignments
        const companyToRoleUuid = {};
        (role.companyUuids || []).forEach((companyUuid, i) => {
          companyToRoleUuid[companyUuid] = role.uuids
            ? role.uuids[i]
            : role.uuid;
        });

        const manageableSet = new Set(manageableCompanyUuids);
        const oldManagedSet = new Set(
          (role.companyUuids || []).filter((uuid) => manageableSet.has(uuid))
        );
        const newSet = new Set(selectedCompanyUuids);

        const toUpdate = [...newSet].filter((uuid) => oldManagedSet.has(uuid));
        const toCreate = [...newSet].filter((uuid) => !oldManagedSet.has(uuid));
        const toDelete = [...oldManagedSet].filter((uuid) => !newSet.has(uuid));

        const promises = [
          ...toUpdate.map((companyUuid) =>
            FieldConfigurationAPI.updateRole(
              companyToRoleUuid[companyUuid],
              apiRoleData,
              companyUuid
            ).then((r) => ({ op: "update", companyUuid, r }))
          ),
          ...toCreate.map((companyUuid) =>
            FieldConfigurationAPI.createRole(apiRoleData, companyUuid).then(
              (r) => ({ op: "create", companyUuid, r })
            )
          ),
          ...toDelete.map((companyUuid) =>
            FieldConfigurationAPI.deleteRole(
              companyToRoleUuid[companyUuid],
              companyUuid
            ).then((r) => ({ op: "delete", companyUuid, r }))
          ),
        ];

        const results = await Promise.all(promises);

        // Check for 401
        const unauth = results.find((res) => res.r.status === 401);
        if (unauth && checkAuthAndRedirect(unauth.r)) {
          return { success: false, error: "Unauthorized" };
        }

        // Check for 403 (license errors)
        const licenseErr = results.find(
          (res) => res.r.status === 403 && res.r.data
        );
        if (licenseErr) {
          dispatch({ type: ACTIONS.SET_ROLES_LOADING, payload: false });
          return {
            success: false,
            status: 403,
            error: licenseErr.r.data.message || "Insufficient license seats",
            errors: licenseErr.r.data.errors || [],
            details: transformLicenseDetails(licenseErr.r.data.details),
          };
        }

        // Check for 409 on delete — role still has users assigned in that company
        const deleteConflict = results.find(
          ({ op, r }) => op === "delete" && r.status === 409
        );
        if (deleteConflict) {
          dispatch({ type: ACTIONS.SET_ROLES_LOADING, payload: false });
          const companyName =
            (companiesMap || {})[deleteConflict.companyUuid] || "that company";
          return {
            success: false,
            error: `Cannot remove "${companyName}" because users are still assigned to this role there. Please reassign those users to a different role first.`,
          };
        }

        const allSucceeded = results.every(({ op, r }) => {
          if (op === "delete") return r.status === 200 || r.status === 204;
          if (op === "create") return r.status === 200 || r.status === 201;
          return r.status === 200;
        });

        if (allSucceeded) {
          const rolesResponse = await FieldConfigurationAPI.getRoles();
          let groupedRole = null;
          if (rolesResponse.status === 200) {
            const transformedRoles = Array.isArray(rolesResponse.data)
              ? rolesResponse.data.map(transformApiRoleToUi)
              : [];
            dispatch({ type: ACTIONS.SET_ROLES, payload: transformedRoles });

            // Build a grouped role from refreshed data so the caller can update
            // selectedItem without losing companyUuids.
            const managedUuids = new Set([
              ...toUpdate.map((cu) => companyToRoleUuid[cu]),
              ...results
                .filter(
                  ({ op, r }) =>
                    op === "create" && (r.status === 200 || r.status === 201)
                )
                .map(({ r }) => r.data?.uuid)
                .filter(Boolean),
            ]);
            // Preserve unmanaged-company role UUIDs (unchanged by this operation)
            const unmanagedUuids = new Set(
              (role.companyUuids || [])
                .filter((cu) => !manageableSet.has(cu))
                .map((cu) => companyToRoleUuid[cu])
                .filter(Boolean)
            );
            const allGroupUuids = new Set([...managedUuids, ...unmanagedUuids]);
            const roleGroup = transformedRoles.filter((r) =>
              allGroupUuids.has(r.uuid)
            );
            if (roleGroup.length > 0) {
              groupedRole = {
                ...roleGroup[0],
                uuids: roleGroup.map((r) => r.uuid),
                companyUuids: roleGroup
                  .map((r) => r.company_uuid)
                  .filter(Boolean),
                companyNames: roleGroup.map(
                  (r) =>
                    (companiesMap || {})[r.company_uuid] || r.company_uuid || ""
                ),
                totalUsers: roleGroup.reduce(
                  (s, r) => s + (r.totalUsers || 0),
                  0
                ),
                activeUsers: roleGroup.reduce(
                  (s, r) => s + (r.activeUsers || 0),
                  0
                ),
              };
            }
          }
          await fetchLicenses(true);
          return { success: true, data: groupedRole };
        } else {
          const failed = results.find(({ op, r }) => {
            if (op === "delete") return r.status !== 200 && r.status !== 204;
            if (op === "create") return r.status !== 200 && r.status !== 201;
            return r.status !== 200;
          });
          const error = `Failed to save role: ${failed?.r?.status}`;
          dispatch({ type: ACTIONS.SET_ROLES_ERROR, payload: error });
          return { success: false, error };
        }
      } catch (error) {
        const errorMsg = error.message || "Unknown error saving role";
        dispatch({ type: ACTIONS.SET_ROLES_ERROR, payload: errorMsg });
        return { success: false, error: errorMsg };
      }
    },
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // ============================================
  // USERS METHODS
  // ============================================

  const fetchUsers = useCallback(
    async (forceRefresh = false) => {
      // Use cache if valid and not forcing refresh
      if (!forceRefresh && isCacheValid(state.users.lastFetched)) {
        return { success: true, data: state.users.data };
      }

      dispatch({ type: ACTIONS.SET_USERS_LOADING, payload: true });

      try {
        // For user-scoped tokens with multiple configure-users companies,
        // fetch each company separately and merge results.
        const configureUsersUuids = Object.keys(
          state.companies.configureUsersData || {}
        );
        const isUserScoped =
          sessionStorage.getItem("field_token_scope") === "user";
        const needsPerCompanyFetch =
          isUserScoped && configureUsersUuids.length > 1;

        let allApiUsers = [];

        if (needsPerCompanyFetch) {
          const responses = await Promise.all(
            configureUsersUuids.map((uuid) =>
              FieldConfigurationAPI.getUsers(uuid)
            )
          );
          // Check for 401 in any response
          for (const r of responses) {
            if (checkAuthAndRedirect(r)) {
              return { success: false, error: "Unauthorized" };
            }
          }
          const failedResponse = responses.find((r) => r.status !== 200);
          if (failedResponse) {
            const error = `Failed to fetch users: ${failedResponse.status}`;
            dispatch({ type: ACTIONS.SET_USERS_ERROR, payload: error });
            return { success: false, error };
          }
          allApiUsers = responses.flatMap((r) =>
            Array.isArray(r.data) ? r.data : []
          );
        } else {
          const response = await FieldConfigurationAPI.getUsers();
          if (checkAuthAndRedirect(response)) {
            return { success: false, error: "Unauthorized" };
          }
          if (response.status !== 200) {
            const error = `Failed to fetch users: ${response.status}`;
            dispatch({ type: ACTIONS.SET_USERS_ERROR, payload: error });
            return { success: false, error };
          }
          allApiUsers = Array.isArray(response.data) ? response.data : [];
        }

        // Build a roles map for lookup (uuid -> role)
        const rolesMap = state.roles.data.reduce((map, role) => {
          map[role.uuid] = role;
          return map;
        }, {});

        // Transform API format to UI format
        const transformedUsers = allApiUsers.map((user) =>
          transformApiUserToUi(user, rolesMap)
        );
        dispatch({ type: ACTIONS.SET_USERS, payload: transformedUsers });
        return { success: true, data: transformedUsers };
      } catch (error) {
        const errorMsg = error.message || "Unknown error fetching users";
        dispatch({ type: ACTIONS.SET_USERS_ERROR, payload: errorMsg });
        return { success: false, error: errorMsg };
      }
    },
    [
      state.users.lastFetched,
      state.users.data,
      state.roles.data,
      state.companies.configureUsersData,
    ]
  );

  const getUser = useCallback(async (uuid) => {
    try {
      const response = await FieldConfigurationAPI.getUser(uuid);

      // Check for 401 and redirect if unauthorized
      if (checkAuthAndRedirect(response)) {
        return { success: false, error: "Unauthorized" };
      }

      if (response.status === 200) {
        return { success: true, data: response.data };
      } else {
        const error = `Failed to fetch user: ${response.status}`;
        return { success: false, error };
      }
    } catch (error) {
      const errorMsg = error.message || "Unknown error fetching user";
      return { success: false, error: errorMsg };
    }
  }, []);

  // Helper to transform license error details from API format to UI format
  const transformLicenseDetails = (apiDetails) => {
    if (!apiDetails || !Array.isArray(apiDetails)) return [];

    const featureNameMap = {
      timesheets: "Time Entry",
      expenses: "Expense",
      projectmgmt: "Project Management",
    };

    return apiDetails.map((detail) => ({
      name: featureNameMap[detail.feature] || detail.feature,
      used: detail.current || 0,
      total: detail.limit || 0,
      available: detail.available || 0,
      needed: detail.needed || 0,
    }));
  };

  const fetchGroupsForCompany = useCallback(async (companyUuid) => {
    try {
      const response = await FieldConfigurationAPI.getGroups(companyUuid);
      if (response.status === 200 && response.data) {
        return { success: true, data: response.data };
      }
      return { success: false, data: [] };
    } catch (err) {
      return { success: false, data: [] };
    }
  }, []);

  const createUser = useCallback(
    async (userData, targetCompanyUuid) => {
      dispatch({ type: ACTIONS.SET_USERS_LOADING, payload: true });

      try {
        // Transform UI format to API format
        const apiUserData = transformUiUserToApi(userData);
        const response = await FieldConfigurationAPI.createUser(
          apiUserData,
          targetCompanyUuid
        );

        // Check for 401 and redirect if unauthorized
        if (checkAuthAndRedirect(response)) {
          return { success: false, error: "Unauthorized" };
        }

        if (response.status === 200 || response.status === 201) {
          // Build roles map for transformation
          const rolesMap = state.roles.data.reduce((map, role) => {
            map[role.uuid] = role;
            return map;
          }, {});

          // Transform API response back to UI format
          const transformedUser = transformApiUserToUi(response.data, rolesMap);
          dispatch({ type: ACTIONS.ADD_USER, payload: transformedUser });
          await fetchLicenses(true);
          dispatch({ type: ACTIONS.SET_USERS_LOADING, payload: false });
          return { success: true, data: transformedUser };
        } else if (response.status === 403 && response.data) {
          // License limit error - return detailed error information
          dispatch({ type: ACTIONS.SET_USERS_LOADING, payload: false });
          return {
            success: false,
            status: 403,
            error: response.data.message || "Insufficient license seats",
            errors: response.data.errors || [],
            details: transformLicenseDetails(response.data.details),
          };
        } else {
          const error = `Failed to create user: ${response.status}`;
          dispatch({ type: ACTIONS.SET_USERS_ERROR, payload: error });
          return { success: false, error };
        }
      } catch (error) {
        const errorMsg = error.message || "Unknown error creating user";
        dispatch({ type: ACTIONS.SET_USERS_ERROR, payload: errorMsg });
        return { success: false, error: errorMsg };
      }
    },
    [state.roles.data]
  );

  const updateUser = useCallback(
    async (uuid, userData, targetCompanyUuid) => {
      dispatch({ type: ACTIONS.SET_USERS_LOADING, payload: true });

      try {
        // Transform UI format to API format
        const apiUserData = transformUiUserToApi(userData);
        const response = await FieldConfigurationAPI.updateUser(
          uuid,
          apiUserData,
          targetCompanyUuid
        );

        // Check for 401 and redirect if unauthorized
        if (checkAuthAndRedirect(response)) {
          return { success: false, error: "Unauthorized" };
        }

        if (response.status === 200) {
          // Build roles map for transformation
          const rolesMap = state.roles.data.reduce((map, role) => {
            map[role.uuid] = role;
            return map;
          }, {});

          // Transform API response back to UI format
          const transformedUser = transformApiUserToUi(response.data, rolesMap);
          dispatch({ type: ACTIONS.UPDATE_USER, payload: transformedUser });
          await fetchLicenses(true);
          dispatch({ type: ACTIONS.SET_USERS_LOADING, payload: false });
          return { success: true, data: transformedUser };
        } else if (response.status === 403 && response.data) {
          // License limit error - return detailed error information
          dispatch({ type: ACTIONS.SET_USERS_LOADING, payload: false });
          return {
            success: false,
            status: 403,
            error: response.data.message || "Insufficient license seats",
            errors: response.data.errors || [],
            details: transformLicenseDetails(response.data.details),
          };
        } else {
          const error = `Failed to update user: ${response.status}`;
          dispatch({ type: ACTIONS.SET_USERS_ERROR, payload: error });
          return { success: false, error };
        }
      } catch (error) {
        const errorMsg = error.message || "Unknown error updating user";
        dispatch({ type: ACTIONS.SET_USERS_ERROR, payload: errorMsg });
        return { success: false, error: errorMsg };
      }
    },
    [state.roles.data]
  );

  const deleteUser = useCallback(async (uuid) => {
    dispatch({ type: ACTIONS.SET_USERS_LOADING, payload: true });

    try {
      const response = await FieldConfigurationAPI.deleteUser(uuid);

      // Check for 401 and redirect if unauthorized
      if (checkAuthAndRedirect(response)) {
        return { success: false, error: "Unauthorized" };
      }

      if (response.status === 200 || response.status === 204) {
        dispatch({ type: ACTIONS.DELETE_USER, payload: uuid });
        await fetchLicenses(true);
        dispatch({ type: ACTIONS.SET_USERS_LOADING, payload: false });
        return { success: true };
      } else {
        const error = `Failed to delete user: ${response.status}`;
        dispatch({ type: ACTIONS.SET_USERS_ERROR, payload: error });
        return { success: false, error };
      }
    } catch (error) {
      const errorMsg = error.message || "Unknown error deleting user";
      dispatch({ type: ACTIONS.SET_USERS_ERROR, payload: errorMsg });
      return { success: false, error: errorMsg };
    }
  }, []);

  const inviteUser = useCallback(async (uuid) => {
    try {
      const response = await FieldConfigurationAPI.inviteUser(uuid);

      // Check for 401 and redirect if unauthorized
      if (checkAuthAndRedirect(response)) {
        return { success: false, error: "Unauthorized" };
      }

      if (response.status === 200) {
        return { success: true, data: response.data };
      } else {
        const error = `Failed to invite user: ${response.status}`;
        return { success: false, error };
      }
    } catch (error) {
      const errorMsg = error.message || "Unknown error inviting user";
      return { success: false, error: errorMsg };
    }
  }, []);

  const bulkUpdateUsers = useCallback(async (userUpdates) => {
    dispatch({ type: ACTIONS.SET_USERS_LOADING, payload: true });

    try {
      const response = await FieldConfigurationAPI.bulkUpdateUsers(userUpdates);

      // Check for 401 and redirect if unauthorized
      if (checkAuthAndRedirect(response)) {
        return { success: false, error: "Unauthorized" };
      }

      if (response.status === 200 || response.status === 207) {
        // Check if any failed updates were due to license limits (403)
        const licenseErrors = [];
        const licenseDetails = [];
        if (response.data?.results) {
          response.data.results.forEach((result) => {
            if (
              !result.success &&
              result.status === 403 &&
              result.data?.errors
            ) {
              licenseErrors.push(...result.data.errors);
              // Also collect details if available
              if (result.data?.details) {
                licenseDetails.push(...result.data.details);
              }
            }
          });
        }

        // If there were license errors, return them
        if (licenseErrors.length > 0) {
          dispatch({ type: ACTIONS.SET_USERS_LOADING, payload: false });
          return {
            success: false,
            status: 403,
            error: "Insufficient license seats to enable requested users.",
            errors: licenseErrors,
            details: transformLicenseDetails(licenseDetails),
            data: response.data,
          };
        }

        // Refresh users list after bulk update
        await fetchUsers(true);
        await fetchLicenses(true);
        return { success: true, data: response.data };
      } else {
        const error = `Failed to update users: ${response.status}`;
        dispatch({ type: ACTIONS.SET_USERS_ERROR, payload: error });
        return { success: false, error };
      }
    } catch (error) {
      const errorMsg = error.message || "Unknown error updating users";
      dispatch({ type: ACTIONS.SET_USERS_ERROR, payload: errorMsg });
      return { success: false, error: errorMsg };
    }
  }, []);

  const bulkInviteUsers = useCallback(async (userUuids) => {
    try {
      const response = await FieldConfigurationAPI.bulkInviteUsers(userUuids);

      // Check for 401 and redirect if unauthorized
      if (checkAuthAndRedirect(response)) {
        return { success: false, error: "Unauthorized" };
      }

      if (response.status === 200 || response.status === 207) {
        return { success: true, data: response.data };
      } else {
        const error = `Failed to invite users: ${response.status}`;
        return { success: false, error };
      }
    } catch (error) {
      const errorMsg = error.message || "Unknown error inviting users";
      return { success: false, error: errorMsg };
    }
  }, []);

  // Saves a user with company assignment changes.
  // selectedCompanyUuids = companies that should be active after save.
  // manageableCompanyUuids = all companies where logged-in user has configure_users.
  const saveUserWithCompanyAssignments = useCallback(
    async (
      user,
      formData,
      selectedCompanyUuids,
      manageableCompanyUuids,
      companiesMap,
      rolesData,
      createRoleForCompanies = []
    ) => {
      dispatch({ type: ACTIONS.SET_USERS_LOADING, payload: true });

      try {
        const apiUserData = transformUiUserToApi(formData);

        // Roles are per-company — find the equivalent role UUID in each target company
        // by matching on role name.
        const selectedRoleUuid = apiUserData.role_uuid;
        const selectedRole = (rolesData || []).find(
          (r) => r.uuid === selectedRoleUuid
        );
        const selectedRoleName = selectedRole?.name;
        const companyRoleUuidMap = {};
        if (selectedRoleName) {
          (rolesData || []).forEach((role) => {
            if (role.name === selectedRoleName && role.company_uuid) {
              companyRoleUuidMap[role.company_uuid] = role.uuid;
            }
          });
        }

        // Create role in companies where it doesn't exist yet
        if (createRoleForCompanies && createRoleForCompanies.length > 0) {
          const sourceRoleUi = (rolesData || []).find(
            (r) => r.uuid === selectedRoleUuid
          );
          if (sourceRoleUi) {
            const apiRoleData = transformUiRoleToApi(sourceRoleUi);
            const roleCreateResults = await Promise.all(
              createRoleForCompanies.map((companyUuid) =>
                FieldConfigurationAPI.createRole(apiRoleData, companyUuid).then(
                  (r) => ({ companyUuid, r })
                )
              )
            );
            const failedRoleCreate = roleCreateResults.find(
              ({ r }) => r.status !== 200 && r.status !== 201
            );
            if (failedRoleCreate) {
              dispatch({ type: ACTIONS.SET_USERS_LOADING, payload: false });
              return {
                success: false,
                error: `Failed to create role in company: ${failedRoleCreate.r.status}`,
              };
            }
            roleCreateResults.forEach(({ companyUuid, r }) => {
              if (r.data?.uuid) companyRoleUuidMap[companyUuid] = r.data.uuid;
            });
          }
        }

        const getRoleForCompany = (companyUuid) =>
          companyRoleUuidMap[companyUuid] || selectedRoleUuid;

        // Build map of companyUuid -> user record UUID for existing assignments
        const companyToUserUuid = {};
        (user.companyUuids || []).forEach((companyUuid, i) => {
          companyToUserUuid[companyUuid] = user.uuids
            ? user.uuids[i]
            : user.uuid;
        });

        const manageableSet = new Set(manageableCompanyUuids);
        const oldManagedSet = new Set(
          (user.companyUuids || []).filter((uuid) => manageableSet.has(uuid))
        );
        const newSet = new Set(selectedCompanyUuids);

        // selected + already exists → update (PUT with enabled: true)
        const toUpdate = [...newSet].filter((uuid) => oldManagedSet.has(uuid));
        // selected + doesn't exist → create (POST with enabled: true)
        const toCreate = [...newSet].filter((uuid) => !oldManagedSet.has(uuid));
        // existed + deselected → deactivate (PUT with enabled: false)
        const toDeactivate = [...oldManagedSet].filter(
          (uuid) => !newSet.has(uuid)
        );

        const promises = [
          ...toUpdate.map((companyUuid) =>
            FieldConfigurationAPI.updateUser(
              companyToUserUuid[companyUuid],
              {
                ...apiUserData,
                enabled: true,
                role_uuid: getRoleForCompany(companyUuid),
              },
              companyUuid
            ).then((r) => ({ op: "update", companyUuid, r }))
          ),
          ...toCreate.map((companyUuid) =>
            FieldConfigurationAPI.createUser(
              {
                ...apiUserData,
                enabled: true,
                role_uuid: getRoleForCompany(companyUuid),
              },
              companyUuid
            ).then((r) => ({ op: "create", companyUuid, r }))
          ),
          ...toDeactivate.map((companyUuid) =>
            FieldConfigurationAPI.updateUser(
              companyToUserUuid[companyUuid],
              { enabled: false },
              companyUuid
            ).then((r) => ({ op: "deactivate", companyUuid, r }))
          ),
        ];

        const results = await Promise.all(promises);

        // Check for 401
        const unauth = results.find((res) => res.r.status === 401);
        if (unauth && checkAuthAndRedirect(unauth.r)) {
          return { success: false, error: "Unauthorized" };
        }

        // Check for 403 (license errors)
        const licenseErr = results.find(
          (res) => res.r.status === 403 && res.r.data
        );
        if (licenseErr) {
          dispatch({ type: ACTIONS.SET_USERS_LOADING, payload: false });
          return {
            success: false,
            status: 403,
            error: licenseErr.r.data.message || "Insufficient license seats",
            errors: licenseErr.r.data.errors || [],
            details: transformLicenseDetails(licenseErr.r.data.details),
          };
        }

        const allSucceeded = results.every(({ op, r }) => {
          if (op === "create") return r.status === 200 || r.status === 201;
          return r.status === 200;
        });

        if (allSucceeded) {
          const usersResult = await fetchUsers(true);
          let groupedUser = null;
          if (usersResult.success) {
            const transformedUsers = usersResult.data || [];

            // Build set of all user record UUIDs in this group
            const managedUuids = new Set([
              ...toUpdate.map((cu) => companyToUserUuid[cu]),
              ...toDeactivate.map((cu) => companyToUserUuid[cu]),
              ...results
                .filter(
                  ({ op, r }) =>
                    op === "create" && (r.status === 200 || r.status === 201)
                )
                .map(({ r }) => r.data?.uuid)
                .filter(Boolean),
            ]);
            // Preserve unmanaged-company user UUIDs (unchanged by this operation)
            const unmanagedUuids = new Set(
              (user.companyUuids || [])
                .filter((cu) => !manageableSet.has(cu))
                .map((cu) => companyToUserUuid[cu])
                .filter(Boolean)
            );
            const allGroupUuids = new Set([...managedUuids, ...unmanagedUuids]);
            const userGroup = transformedUsers.filter((u) =>
              allGroupUuids.has(u.uuid)
            );
            if (userGroup.length > 0) {
              groupedUser = {
                ...userGroup[0],
                uuids: userGroup.map((u) => u.uuid),
                companyUuids: userGroup
                  .map((u) => u.company_uuid)
                  .filter(Boolean),
                companyNames: userGroup.map(
                  (u) =>
                    (companiesMap || {})[u.company_uuid] || u.company_uuid || ""
                ),
              };
            }
          }
          await fetchLicenses(true);
          return { success: true, data: groupedUser };
        } else {
          const failed = results.find(({ op, r }) => {
            if (op === "create") return r.status !== 200 && r.status !== 201;
            return r.status !== 200;
          });
          const error = `Failed to save user: ${failed?.r?.status}`;
          dispatch({ type: ACTIONS.SET_USERS_ERROR, payload: error });
          return { success: false, error };
        }
      } catch (error) {
        const errorMsg = error.message || "Unknown error saving user";
        dispatch({ type: ACTIONS.SET_USERS_ERROR, payload: errorMsg });
        return { success: false, error: errorMsg };
      }
    },
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // ============================================
  // LICENSES METHODS
  // ============================================

  const fetchLicenses = useCallback(
    async (forceRefresh = false) => {
      // Use cache if valid and not forcing refresh
      if (!forceRefresh && isCacheValid(state.licenses.lastFetched)) {
        return { success: true, data: state.licenses.data };
      }

      dispatch({ type: ACTIONS.SET_LICENSES_LOADING, payload: true });

      try {
        const response = await FieldConfigurationAPI.getLicenses();
        console.log(
          "[FieldConfig] fetchLicenses response status:",
          response.status
        );

        // 401 on licenses means the endpoint is not available for this token
        // scope — do not log the user out, just mark licenses as unavailable.
        if (response.status === 401) {
          dispatch({
            type: ACTIONS.SET_LICENSES_ERROR,
            payload: "unavailable",
          });
          return { success: false, error: "unavailable" };
        }

        // All other auth failures should still redirect to login
        if (checkAuthAndRedirect(response)) {
          return { success: false, error: "Unauthorized" };
        }

        if (response.status === 200) {
          // Store the licenses data directly (no transformation needed)
          const licensesData = response.data?.licenses || {};
          dispatch({ type: ACTIONS.SET_LICENSES, payload: licensesData });
          return { success: true, data: licensesData };
        } else {
          const error = `Failed to fetch licenses: ${response.status}`;
          dispatch({ type: ACTIONS.SET_LICENSES_ERROR, payload: error });
          return { success: false, error };
        }
      } catch (error) {
        const errorMsg = error.message || "Unknown error fetching licenses";
        dispatch({ type: ACTIONS.SET_LICENSES_ERROR, payload: errorMsg });
        return { success: false, error: errorMsg };
      }
    },
    [state.licenses.lastFetched, state.licenses.data]
  );

  // ============================================
  // ADMIN PERMISSIONS
  // ============================================

  const fetchAdminPermissions = useCallback(async () => {
    if (state.adminPermissions.loaded) {
      return { success: true, data: state.adminPermissions.data };
    }

    dispatch({ type: ACTIONS.SET_ADMIN_PERMISSIONS_LOADING, payload: true });

    try {
      // Decode JWT to determine token scope without an extra API call
      const token = sessionStorage.getItem("token");
      let scope = null;
      try {
        const payload = token.split(".")[1];
        const decoded = JSON.parse(
          atob(payload.replace(/-/g, "+").replace(/_/g, "/"))
        );
        scope = decoded?.scope;
        console.log(
          "[FieldConfig] fetchAdminPermissions decoded JWT:",
          decoded
        );
      } catch {
        // Malformed token — let the session call handle the 401
      }
      console.log("[FieldConfig] fetchAdminPermissions token scope:", scope);

      // Company-scope tokens have unrestricted access to everything
      if (scope === "company") {
        sessionStorage.setItem("field_token_scope", "company");
        sessionStorage.removeItem("field_company_uuid");
        const permissions = {
          enabled: true,
          view_licenses: true,
          configure_roles: true,
          configure_users: true,
          configure_access_groups: true,
        };
        dispatch({
          type: ACTIONS.SET_ADMIN_PERMISSIONS,
          payload: permissions,
        });

        // Fetch company names for company-scoped tokens
        const authResponse = await FieldConfigurationAPI.getCompanies();
        if (authResponse.status === 200 && authResponse.data) {
          const companiesList = authResponse.data.companies || [];
          const companiesMap = Array.isArray(companiesList)
            ? companiesList.reduce((map, c) => {
                if (c.uuid) map[c.uuid] = c.name || c.uuid;
                return map;
              }, {})
            : {};
          const companiesCodesMap = Array.isArray(companiesList)
            ? companiesList.reduce((map, c) => {
                if (c.uuid && c.code != null) map[c.uuid] = c.code;
                return map;
              }, {})
            : {};
          // Company-scoped tokens have full access — all maps equal data
          dispatch({
            type: ACTIONS.SET_COMPANIES,
            payload: {
              data: companiesMap,
              configureRolesData: companiesMap,
              configureUsersData: companiesMap,
              codesMap: companiesCodesMap,
            },
          });
        }

        return { success: true, data: permissions };
      }

      // Read company_uuid from URL query string (?company_uuid=...)
      const companyUuid = new URLSearchParams(window.location.search).get(
        "company_uuid"
      );
      console.log(
        "[FieldConfig] fetchAdminPermissions company_uuid from URL:",
        companyUuid
      );

      // User-scope token — fetch session to get role permissions
      const response = await FieldConfigurationAPI.getMyPermissions();
      console.log(
        "[FieldConfig] fetchAdminPermissions /user/session response status:",
        response.status,
        "data:",
        response.data
      );

      if (checkAuthAndRedirect(response)) {
        return { success: false, error: "Unauthorized" };
      }

      if (response.status === 200 && response.data) {
        const companyUsers = response.data.users || [];

        // 1. Match the company_uuid from the URL query string
        let companyUser = companyUuid
          ? companyUsers.find((cu) => cu.company?.uuid === companyUuid)
          : null;

        // 2. Fall back to first company_user with administration enabled
        if (!companyUser) {
          companyUser = companyUsers.find(
            (cu) => cu.role?.permissions?.administration?.enabled === true
          );
        }

        const administration =
          companyUser?.role?.permissions?.administration || null;
        console.log(
          "[FieldConfig] fetchAdminPermissions resolved companyUser:",
          companyUser?.uuid,
          "administration:",
          administration
        );

        if (!administration?.enabled) {
          // No admin access found — unauthorized
          dispatch({ type: ACTIONS.SET_ADMIN_PERMISSIONS, payload: null });
          return { success: false, error: "Unauthorized" };
        }

        // Store scope and resolved company UUID/name for API route selection
        sessionStorage.setItem("field_token_scope", "user");
        sessionStorage.setItem("field_company_uuid", companyUser.company.uuid);
        if (companyUser.company.name) {
          sessionStorage.setItem(
            "field_company_name",
            companyUser.company.name
          );
        }

        // Build UUID→name map from all accessible companies
        const companiesMap = {};
        const configureRolesMap = {};
        const configureUsersMap = {};
        const codesMap = {};
        companyUsers.forEach((cu) => {
          if (cu.company?.uuid) {
            const name = cu.company.name || cu.company.uuid;
            companiesMap[cu.company.uuid] = name;
            if (cu.company.code != null) {
              codesMap[cu.company.uuid] = cu.company.code;
            }
            if (
              cu.role?.permissions?.administration?.configure_roles === true
            ) {
              configureRolesMap[cu.company.uuid] = name;
            }
            if (
              cu.role?.permissions?.administration?.configure_users === true
            ) {
              configureUsersMap[cu.company.uuid] = name;
            }
          }
        });
        dispatch({
          type: ACTIONS.SET_COMPANIES,
          payload: {
            data: companiesMap,
            configureRolesData: configureRolesMap,
            configureUsersData: configureUsersMap,
            codesMap,
          },
        });

        dispatch({
          type: ACTIONS.SET_ADMIN_PERMISSIONS,
          payload: administration,
        });
        return { success: true, data: administration };
      } else {
        dispatch({ type: ACTIONS.SET_ADMIN_PERMISSIONS, payload: null });
        return { success: false, error: "Could not load permissions" };
      }
    } catch (error) {
      dispatch({ type: ACTIONS.SET_ADMIN_PERMISSIONS, payload: null });
      return { success: false, error: error.message };
    }
  }, [state.adminPermissions.loaded, state.adminPermissions.data]);

  // ============================================
  // UTILITY METHODS
  // ============================================

  const invalidateCache = useCallback(() => {
    dispatch({ type: ACTIONS.INVALIDATE_CACHE });
  }, []);

  const value = {
    // State
    roles: state.roles,
    users: state.users,
    companies: state.companies,
    accessGroups: state.accessGroups, // For future use
    licenses: state.licenses,
    adminPermissions: state.adminPermissions,

    // Roles methods
    fetchRoles,
    createRole,
    updateRole,
    deleteRole,
    bulkUpdateRoles,
    saveRoleWithCompanyAssignments,

    // Users methods
    fetchUsers,
    getUser,
    createUser,
    updateUser,
    deleteUser,
    inviteUser,
    bulkUpdateUsers,
    bulkInviteUsers,
    saveUserWithCompanyAssignments,
    fetchGroupsForCompany,

    // Licenses methods
    fetchLicenses,

    // Admin permissions methods
    fetchAdminPermissions,

    // Utility methods
    invalidateCache,
  };

  return (
    <FieldConfigurationContext.Provider value={value}>
      {children}
    </FieldConfigurationContext.Provider>
  );
}
